import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import * as db from '../services/indexedDB';
import { syncFromServer, processSyncQueue } from '../services/syncService';

const OfflineContext = createContext({
  isOnline: true,
  isSyncing: false,
  isReady: false, // New: indicates app is ready to display data
  isAuthExpired: false, // New: indicates Cloudflare Access token has expired
  pendingSyncCount: 0,
  lastSyncTime: null,
  triggerSync: () => {},
  updatePendingCount: () => {},
  dismissAuthExpired: () => {},
  handleReLogin: () => {},
});

// Detect if running as installed PWA
function isStandaloneMode() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
}

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isDbInitialized, setIsDbInitialized] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isAuthExpired, setIsAuthExpired] = useState(false);
  
  // Use refs to avoid dependency issues in callbacks
  const isSyncingRef = useRef(false);
  const hasInitialSyncRun = useRef(false);
  
  // App is ready when DB is initialized AND either:
  // - We've loaded data from sync, OR
  // - We're offline and have checked for cached data
  const isReady = isDbInitialized && isDataLoaded;
  
  // Dismiss auth expiry modal (continue in read-only mode)
  const dismissAuthExpired = useCallback(() => {
    setIsAuthExpired(false);
  }, []);
  
  // Handle re-login (redirect to app URL which will trigger Cloudflare Access)
  const handleReLogin = useCallback(() => {
    // Clear auth expiry state first
    setIsAuthExpired(false);
    // Redirect to app root - Cloudflare Access will intercept and show login
    window.location.href = '/';
  }, []);

  // Initialize IndexedDB on mount - with retry for PWA
  useEffect(() => {
    let mounted = true;
    let retryCount = 0;
    const maxRetries = 3;
    
    async function initializeDB() {
      try {
        await db.initDB();
        if (!mounted) return;
        
        console.log('[Offline] IndexedDB initialized');
        setIsDbInitialized(true);
        
        // Check if we have cached data
        const hasData = await db.hasOfflineData();
        if (hasData) {
          console.log('[Offline] Found cached data in IndexedDB');
          setIsDataLoaded(true);
        }
        
        updatePendingCount();
        loadLastSyncTime();
      } catch (err) {
        console.error('[Offline] Failed to initialize IndexedDB:', err);
        
        // Retry in PWA mode as IndexedDB can be slow to initialize
        if (isStandaloneMode() && retryCount < maxRetries) {
          retryCount++;
          console.log(`[Offline] Retrying IndexedDB init (${retryCount}/${maxRetries})...`);
          setTimeout(initializeDB, 500 * retryCount);
          return;
        }
        
        if (mounted) {
          setIsDbInitialized(true); // Continue without IndexedDB
          setIsDataLoaded(true); // Allow app to render
        }
      }
    }
    
    initializeDB();
    
    return () => { mounted = false; };
  }, []);

  const updatePendingCount = useCallback(async () => {
    try {
      const count = await db.getPendingSyncCount();
      setPendingSyncCount(count);
    } catch (err) {
      // IndexedDB not ready, ignore
    }
  }, []);

  async function loadLastSyncTime() {
    try {
      const time = await db.getMetadata('lastSync');
      setLastSyncTime(time);
    } catch (err) {
      // IndexedDB not ready, ignore
    }
  }

  // Full sync function - stable reference using ref for isSyncing check
  const triggerSync = useCallback(async () => {
    if (!navigator.onLine || isSyncingRef.current) {
      console.log('[Offline] Skipping sync - offline or already syncing');
      // Even if offline, mark data as loaded so UI can render
      setIsDataLoaded(true);
      return;
    }
    
    isSyncingRef.current = true;
    setIsSyncing(true);
    console.log('[Offline] Starting sync...');
    
    try {
      // First, check and push any pending changes
      const currentPendingCount = await db.getPendingSyncCount();
      if (currentPendingCount > 0) {
        console.log('[Offline] Processing sync queue...');
        const result = await processSyncQueue();
        console.log('[Offline] Sync queue processed:', result);
      }
      
      // Then pull fresh data from server
      console.log('[Offline] Syncing from server...');
      await syncFromServer();
      
      // Update state
      await updatePendingCount();
      await loadLastSyncTime();
      
      // Mark data as loaded
      setIsDataLoaded(true);
      
      // Clear any previous auth expiry state on successful sync
      setIsAuthExpired(false);
      
      console.log('[Offline] Sync complete');
    } catch (error) {
      console.error('[Offline] Sync failed:', error);
      
      // Check if this is an auth expiry error
      if (error.isAuthExpired) {
        console.log('[Offline] Auth expired, showing re-login prompt');
        setIsAuthExpired(true);
      }
      
      // Still mark as loaded so UI can render with cached data
      setIsDataLoaded(true);
    } finally {
      isSyncingRef.current = false;
      setIsSyncing(false);
    }
  }, [updatePendingCount]);

  // Handle online/offline events - separate from triggerSync to avoid loops
  useEffect(() => {
    function handleOnline() {
      console.log('[Offline] Back online');
      setIsOnline(true);
      // Small delay to ensure network is stable
      setTimeout(() => {
        triggerSync();
      }, 1000);
    }

    function handleOffline() {
      console.log('[Offline] Gone offline');
      setIsOnline(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [triggerSync]);

  // Initial sync - runs once after DB initialization
  useEffect(() => {
    if (isDbInitialized && !hasInitialSyncRun.current) {
      hasInitialSyncRun.current = true;
      
      if (navigator.onLine) {
        // In PWA mode, sync immediately; in browser, add small delay
        const delay = isStandaloneMode() ? 100 : 1000;
        const timer = setTimeout(() => {
          triggerSync();
        }, delay);
        return () => clearTimeout(timer);
      } else {
        // Offline - just mark as loaded so UI renders with cached data
        setIsDataLoaded(true);
      }
    }
  }, [isDbInitialized, triggerSync]);

  // Periodic pending count update (less frequent to reduce overhead)
  useEffect(() => {
    if (!isDbInitialized) return;
    
    const interval = setInterval(updatePendingCount, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [isDbInitialized, updatePendingCount]);

  return (
    <OfflineContext.Provider value={{ 
      isOnline, 
      isSyncing,
      isReady,
      isAuthExpired,
      pendingSyncCount,
      lastSyncTime,
      triggerSync,
      updatePendingCount,
      dismissAuthExpired,
      handleReLogin,
    }}>
      {children}
    </OfflineContext.Provider>
  );
}

export function useOffline() {
  return useContext(OfflineContext);
}
